/**
 * aiReports.service.ts
 *
 * Client for the AI report builder: run results, plus recurrence (schedules)
 * and sharing. The AI Assistant's `run_report` tool queues a report and links
 * the user to /reports/ai-results/:id; `schedule_report` creates a schedule
 * linked at /reports/ai-schedules/:id.
 *
 * Matches AiContextPageChatController (reportResult/reportList) and
 * AiReportScheduleController on the API side.
 */
import { apiClient } from '@/services/apiClient';

export type AiReportStatus = 'pending' | 'ready' | 'failed' | 'scheduled';

export interface AiReportResult {
  id: number;
  status: AiReportStatus;
  prompt: string;
  columns: string[];
  preview: Array<Record<string, unknown>>;
  previewCount: number;
  rowCount: number | null;
  truncated: boolean;
  /** Tokenized, top-level-navigable download URLs (null until ready / not produced). */
  csvUrl: string | null;
  xlsxUrl: string | null;
  error: string | null;
  createdAt: string;
  /** Only the run's owner may schedule/share it (recipients view + download). */
  canManage: boolean;
  /** Set when this run was produced by (or run-now from) a schedule. */
  scheduleId: number | null;
}

/** A row in the unified Reports list (AI side). */
export interface AiReportListItem {
  /** The run to open (latest run for a schedule; null until a schedule's first fire). */
  id: number | null;
  title: string;
  status: AiReportStatus;
  rowCount: number | null;
  createdAt: string;
  generatedBy: string;
  /** True for a recurring schedule row. */
  recurring: boolean;
  /** True for any schedule row (recurring or one-time future). */
  scheduled: boolean;
  /** True when the report is shared with the caller (they are not the owner). */
  shared: boolean;
  /** Set for schedule rows — the schedule to manage. */
  scheduleId: number | null;
  /** Next run, formatted in the schedule's timezone (schedule rows only). */
  nextRunLocal: string | null;
}

export type ScheduleCadenceUnit = 'day' | 'week' | 'month' | 'year';

export interface AiReportSchedule {
  id: number;
  title: string | null;
  prompt: string;
  recurring: boolean;
  cadenceUnit: ScheduleCadenceUnit | null;
  cadenceCount: number;
  cadenceLabel: string | null;
  timeOfDay: string | null;
  timezone: string;
  nextRunAt: string | null;   // UTC
  nextRunLocal: string | null;
  nextRunInput: string | null;  // local "YYYY-MM-DDTHH:MM" for form inputs
  startDate: string | null;     // local "YYYY-MM-DD" of the next run
  lastReportRunId: number | null;
  runCount: number;
  active: boolean;
  isOwner: boolean;
  generatedBy: string;
  createdAt: string;
  /** Present on show() for the owner. */
  shares?: AiReportShareState;
}

export interface AiReportShareState {
  userIds: number[];
  groupIds: number[];
  buckets: string[];
}

export type ShareTargetType = 'run' | 'schedule';

/** Payload to create a schedule (from an existing run, or from scratch). */
export interface CreateSchedulePayload {
  sourceRunId?: number;
  sql?: string;
  title?: string;
  prompt?: string;
  mode: 'once' | 'recurring';
  runAt?: string;               // once: local "YYYY-MM-DD HH:MM"
  cadenceUnit?: ScheduleCadenceUnit;
  cadenceCount?: number;
  timeOfDay?: string;           // recurring: "HH:MM"
  startDate?: string;           // recurring: "YYYY-MM-DD"
}

export type UpdateSchedulePayload = Partial<
  Pick<CreateSchedulePayload, 'title' | 'mode' | 'runAt' | 'cadenceUnit' | 'cadenceCount' | 'timeOfDay' | 'startDate'>
> & { active?: boolean };

export const aiReportsApi = {
  get: async (id: number): Promise<AiReportResult> => {
    const { data } = await apiClient.get<{ data: AiReportResult }>(`/reports/ai-results/${id}`);
    return data.data;
  },
  list: async (): Promise<AiReportListItem[]> => {
    const { data } = await apiClient.get<{ data: AiReportListItem[] }>('/reports/ai-results');
    return data.data ?? [];
  },

  schedules: {
    list: async (): Promise<AiReportSchedule[]> => {
      const { data } = await apiClient.get<{ data: AiReportSchedule[] }>('/reports/ai-schedules');
      return data.data ?? [];
    },
    get: async (id: number): Promise<AiReportSchedule> => {
      const { data } = await apiClient.get<{ data: AiReportSchedule }>(`/reports/ai-schedules/${id}`);
      return data.data;
    },
    create: async (payload: CreateSchedulePayload): Promise<AiReportSchedule> => {
      const { data } = await apiClient.post<{ data: AiReportSchedule }>('/reports/ai-schedules', payload);
      return data.data;
    },
    update: async (id: number, payload: UpdateSchedulePayload): Promise<AiReportSchedule> => {
      const { data } = await apiClient.put<{ data: AiReportSchedule }>(`/reports/ai-schedules/${id}`, payload);
      return data.data;
    },
    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/reports/ai-schedules/${id}`);
    },
    pause: async (id: number): Promise<AiReportSchedule> => {
      const { data } = await apiClient.post<{ data: AiReportSchedule }>(`/reports/ai-schedules/${id}/pause`);
      return data.data;
    },
    resume: async (id: number): Promise<AiReportSchedule> => {
      const { data } = await apiClient.post<{ data: AiReportSchedule }>(`/reports/ai-schedules/${id}/resume`);
      return data.data;
    },
    runNow: async (id: number): Promise<{ reportId: number; url: string }> => {
      const { data } = await apiClient.post<{ data: { reportId: number; url: string } }>(
        `/reports/ai-schedules/${id}/run-now`,
      );
      return data.data;
    },
  },

  shares: {
    get: async (targetType: ShareTargetType, targetId: number): Promise<AiReportShareState> => {
      const { data } = await apiClient.get<{ data: AiReportShareState }>('/reports/ai-shares', {
        params: { targetType, targetId },
      });
      return data.data;
    },
    put: async (
      targetType: ShareTargetType,
      targetId: number,
      state: AiReportShareState,
    ): Promise<AiReportShareState> => {
      const { data } = await apiClient.put<{ data: AiReportShareState }>('/reports/ai-shares', {
        targetType,
        targetId,
        ...state,
      });
      return data.data;
    },
  },
};
